import React, { useCallback, useState } from "react";
import Button from "@amzn/awsui-components-react/polaris/button";

import { isV2Enabled, setV2Enabled } from "./featureFlag";
import { trackButtonClick } from "../telemetry/telemetry";

/**
 * Compact "Visit X view" affordance that flips the V2 experience flag
 * and reloads. Mounted in both trees:
 *
 * - Legacy `<Content/>` renders "Visit new view" (flip on).
 * - V2 `<AppShell/>` renders "Visit old view" (flip off).
 *
 * Rendered as a fixed-positioned overlay so it sits in the top-right
 * chrome, immediately left of the AI-agent drawer trigger, regardless
 * of route. Cloudscape's `AppLayoutToolbar` has no first-class slot
 * for arbitrary right-hand toolbar utilities — only drawer triggers —
 * so an overlay is the pragmatic path.
 *
 * `App` reads `isV2Enabled()` synchronously at mount, so a soft
 * in-place switch would require lifting the flag into React state
 * and remounting both trees. A full reload keeps this short-lived
 * opt-in surface simple to reason about.
 *
 * Retire alongside `featureFlag.ts` once V2 is fully rolled out.
 */

// The MosaicHeader is a sticky 50px strip at the top (zIndex: 1000).
// Anchor the overlay just below it so the button sits in the
// Cloudscape toolbar row, and stack above the mosaic nav in case any
// child pins itself over the toolbar area. `right: 60px` leaves room
// for the AI-agent drawer trigger; adjust if the drawer strip widens.
const OVERLAY_STYLE: React.CSSProperties = {
  position: "fixed",
  top: "62px",
  right: "60px",
  zIndex: 1001,
};

export const V2Toggle: React.FC = () => {
  // Seed from storage once. The component reloads the page on change,
  // so re-syncing across tabs is not needed within this instance.
  const [enabled, setEnabled] = useState<boolean>(() => isV2Enabled());

  const handleClick = useCallback(async () => {
    const next = !enabled;
    setEnabled(next);
    setV2Enabled(next);
    // Await the tracking call so the Katal log flushes before we
    // reload — otherwise the in-flight XHR is cancelled by the
    // navigation and the event is lost.
    try {
      await trackButtonClick("appShell", "v2Preference", {
        from: enabled ? "v2" : "legacy",
        to: next ? "v2" : "legacy",
      });
    } catch {
      // Telemetry failures never block the user-visible flip.
    }
    // Full reload so `App`'s synchronous flag read picks up the new
    // value and mounts the matching tree from scratch.
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  }, [enabled]);

  // Label describes where clicking takes the user, so it flips with
  // the current side.
  const label = enabled ? "Visit old view" : "Visit new view";

  return (
    <div data-testid="v2-toggle" style={OVERLAY_STYLE}>
      <Button
        variant="normal"
        iconName="external"
        iconAlign="left"
        onClick={handleClick}
        ariaLabel={label}
      >
        {label}
      </Button>
    </div>
  );
};

export default V2Toggle;
